import {
  collection,
  getDocs,
  orderBy,
  query,
  type Query,
  type QuerySnapshot,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import type { z } from "zod";

import { db } from "integrations/firebase/firestore";

import {
  informationItemSchema,
  partnerSchema,
  resourceSchema,
  signalSchema,
  speakerSchema,
  trackSchema,
  venueSchema,
  type InformationItem,
  type Partner,
  type Resource,
  type Signal,
  type Speaker,
  type Track,
  type Venue,
} from "../event-experience/schemas";
import { sortSignals } from "../event-experience/signal-utils";
import {
  adminAgendaItemSchema,
  type AdminAgendaItem,
} from "./agenda-item-schema";

export type AdminContentState<T> =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: T[] };

/**
 * Admin-side reads of the event content collections.
 *
 * Separate from the participant hooks in `event-experience` for two reasons:
 * every one of these needs a `reload` so the list refreshes after a write, and
 * the information items must come back unfiltered — the participant query
 * pins `isPublished == true`, which would hide exactly the drafts an admin is
 * here to work on.
 */
function useAdminSubcollection<T>(
  eventId: string,
  buildQuery: (eventId: string) => Query,
  schema: z.ZodType<T>,
): AdminContentState<T> & {
  reload: () => void;
} {
  const [state, setState] = useState<AdminContentState<T>>({
    status: "loading",
  });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const publish = (snap: QuerySnapshot) => {
      if (cancelled) return;
      const data: T[] = [];
      snap.forEach((d) => {
        const result = schema.safeParse({ id: d.id, ...d.data() });
        if (result.success) data.push(result.data);
      });
      setState({ status: "success", data });
    };
    const reject = (err: unknown) => {
      if (cancelled) return;
      setState({
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load",
      });
    };
    getDocs(buildQuery(eventId)).then(publish).catch(reject);

    return () => {
      cancelled = true;
    };
  }, [eventId, buildQuery, schema, version]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);

  return { ...state, reload };
}

// No orderBy, for the reason signalsQuery gives: Firestore drops a document
// missing the ordered field. `dayKey` arrives after the create, and an item
// with no `startsAt` never gets one, so ordering on either hid sessions the
// organiser needed to see. Ordered on the client by groupAgendaByDay, the one
// ordering rule both agenda tabs share.
const agendaQuery = (id: string) =>
  query(collection(db, "events", id, "agenda_items"));
const tracksQuery = (id: string) =>
  query(collection(db, "events", id, "tracks"), orderBy("displayOrder"));
const speakersQuery = (id: string) =>
  query(collection(db, "events", id, "speakers"), orderBy("name"));
const partnersQuery = (id: string) =>
  query(collection(db, "events", id, "partners"), orderBy("name"));
const venuesQuery = (id: string) =>
  query(collection(db, "events", id, "venues"), orderBy("name"));
const resourcesQuery = (id: string) =>
  query(collection(db, "events", id, "resources"), orderBy("uploadedAt"));
const informationQuery = (id: string) =>
  query(
    collection(db, "events", id, "information_items"),
    orderBy("displayOrder"),
  );
// No orderBy: Firestore drops documents missing the ordered field, and a
// console-seeded signal without displayOrder would vanish from the admin tab
// with nothing to fix it by. Sorted client-side below instead.
const signalsQuery = (id: string) =>
  query(collection(db, "events", id, "signals"));

/**
 * The admin agenda, with a reload.
 *
 * Separate from `useAgenda` in event-experience because that one has no
 * reload, so adding, editing or deleting a session left the list showing what
 * was there when the tab mounted. A first-time organiser read that as the save
 * having failed and added the session again.
 */
export const useAdminAgenda = (eventId: string) =>
  useAdminSubcollection<AdminAgendaItem>(
    eventId,
    agendaQuery,
    adminAgendaItemSchema,
  );
export const useAdminTracks = (eventId: string) =>
  useAdminSubcollection<Track>(eventId, tracksQuery, trackSchema);
/**
 * Sorted the way participants see them, not by name. The admin tab is where
 * `displayOrder` is typed, so it is also the only place the organiser can
 * check what they typed did anything.
 */
export const useAdminSpeakers = (
  eventId: string,
): AdminContentState<Speaker> & {
  reload: () => void;
} => {
  const state = useAdminSubcollection<Speaker>(
    eventId,
    speakersQuery,
    speakerSchema,
  );
  if (state.status !== "success") return state;
  // Deliberately unsorted, like the other five. Sorting here built a new array
  // and a new state object on every render, which is identity churn every
  // consumer pays for. sortSpeakers is applied at each display site instead,
  // which is what the participant page already did.
  return state;
};
export const useAdminPartners = (eventId: string) =>
  useAdminSubcollection<Partner>(eventId, partnersQuery, partnerSchema);
export const useAdminVenues = (eventId: string) =>
  useAdminSubcollection<Venue>(eventId, venuesQuery, venueSchema);
export const useAdminResources = (eventId: string) =>
  useAdminSubcollection<Resource>(eventId, resourcesQuery, resourceSchema);
export const useAdminInformation = (eventId: string) =>
  useAdminSubcollection<InformationItem>(
    eventId,
    informationQuery,
    informationItemSchema,
  );
export const useAdminSignals = (
  eventId: string,
): AdminContentState<Signal> & {
  reload: () => void;
} => {
  const state = useAdminSubcollection<Signal>(
    eventId,
    signalsQuery,
    signalSchema,
  );
  if (state.status !== "success") return state;
  return { ...state, data: sortSignals(state.data) };
};
